// How a room's slim door / window / opening copies are resolved against the
// structure's canonical arrays: the room copy keeps its id, its FK and its
// parentWallIds, and everything else comes from the structure copy.
//
// Moved verbatim out of `shared-lib/estimate-pricing/utils/openingSyncUtils.js`
// (which re-exports all of it) so the take-off and `shared-lib/millwork` read
// ONE copy. `./openingSync.js` keeps an older mirror of these helpers whose
// `APPEARANCE_FIELDS_BY_CATEGORY` lacks the window grille / bay fields and the
// cased opening's trim fields; the geometry it resolves is the same.
//
// Pure.

// All door/window styling lives on structure post-Phase 5. Room copies
// carry only identity + FK + parentWallIds — no trim, no appearance.
// `applyTrimToRoomOpening` becomes a no-op; helpers stay for symmetry
// so callers don't need to branch on opening type.
const DOOR_TRIM_FIELDS = [];
const WINDOW_TRIM_FIELDS = [];

// Trim-field inventory keyed by category. Cased openings have no trim today.
const TRIM_FIELDS_BY_CATEGORY = {
  door: DOOR_TRIM_FIELDS,
  window: WINDOW_TRIM_FIELDS,
  opening: [],
};

// Appearance-field inventory keyed by category. Appearance lives inside
// `category[type][styleKey]` (doorStyle / windowStyle) on the structure
// copy; never on the room copy under the new model.
// Post-Phase-5: all styling — including per-side trim/sill — lives on
// structure. APPEARANCE drives the structure-side write filter.
const APPEARANCE_FIELDS_BY_CATEGORY = {
  door: [
    'style',
    'doorColor',
    'handleStyle',
    'handleColor',
    'hasThreshold',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
  ],
  window: [
    'style',
    'panes',
    // The GRILLE. `panes` alone is not the grille: it is the grid SIZE, and
    // `grillePattern` decides whether a grid is drawn at all (and
    // `upperGrilleOnly` whether the lower sash gets one). Both were missing
    // from this list while `WindowPanel.setGrille` sent them and both
    // renderers read them, so picking Prairie updated the header, redrew the
    // panel's own specimen, persisted `panes`, and dropped the pattern — the
    // window came back with no grille and the panel re-seeded itself to
    // "Colonial". `panels` is the casement pair count, which the elevation
    // schematic reads to decide the centre mullion.
    'grillePattern',
    'upperGrilleOnly',
    'panels',
    // The bay opening angle. Third field to be found missing from this list
    // with a live control wired to it (after `grillePattern` and
    // `upperGrilleOnly` above): the user dragged the angle, the panel and its
    // specimen updated from local state, `pickFields` dropped it, and
    // reselecting the window snapped it back to 45°.
    'bayAngle',
    'frameColor',
    'addHeader',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
    'trimWidthSideA',
    'trimWidthSideB',
    'trimDepthSideA',
    'trimDepthSideB',
    'sillDepthSideA',
    'sillDepthSideB',
    'sillColorSideA',
    'sillColorSideB',
  ],
  // Openings CAN be cased. This list was empty for the whole of Phase 5 —
  // "cased openings have no appearance" — which was true only because
  // nothing rendered a plain opening at all, so there was nothing for a
  // trim choice to change. `Opening.jsx` draws one now, and casing is the
  // only appearance it has: no leaf, no sash, no hardware. Everything else
  // about a pass-through is the hole and the drywall return around it.
  //
  // Same four fields and the same location as a door's, so the two read
  // identically and an opening converted to a door keeps its casing.
  opening: [
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
  ],
};

const pickFields = (obj, fields) => {
  if (!obj) return {};
  const out = {};
  for (const field of fields) {
    if (field in obj && obj[field] !== undefined) {
      out[field] = obj[field];
    }
  }
  return out;
};

/**
 * Build a Map from structure-opening id → structure opening, across all
 * three opening types. Pass the structure-level arrays explicitly — the
 * renderer's `data.doors` is the room-flattened set and keying off that
 * would miss exterior copies. On the renderer side pass
 * `data.structureDoors / structureWindows / structureOpenings`; on the
 * functions side pass `structureData.doors / windows / openings`.
 */
const buildOpeningLookup = ({
  doors = [],
  windows = [],
  openings = [],
} = {}) => {
  const map = new Map();
  for (const d of doors) if (d?.id) map.set(d.id, d);
  for (const w of windows) if (w?.id) map.set(w.id, w);
  for (const o of openings) if (o?.id) map.set(o.id, o);
  return map;
};

/**
 * Hydrate a room's slim door/window/opening arrays into resolved copies
 * (full geometry + appearance) using a structure-side lookup. Use this at
 * the boundary of any pipeline that reads `room.doors[i].center` /
 * `dimensions` (kitchen layout walkway / clearance / wall-segment
 * subtraction). Mirrors the CJS helper in
 * `shared-lib/openings/openingSync.js`.
 */
const hydrateRoomOpenings = (
  room,
  { doors = [], windows = [], openings = [] } = {}
) => {
  if (!room) return room;
  const lookup = buildOpeningLookup({ doors, windows, openings });
  if (lookup.size === 0) return room;
  const resolve = (arr) =>
    (arr || []).map((o) =>
      resolveRoomOpening(o, lookup.get(o?.structureOpeningId))
    );
  return {
    ...room,
    doors: resolve(room.doors),
    windows: resolve(room.windows),
    openings: resolve(room.openings),
  };
};

/**
 * Resolve a room-level opening copy against its structure-level
 * counterpart. Structure is canonical for `center / dimensions / rotation`,
 * opening-itself fields (doorType, doorStyle appearance, …), and swing
 * context (wallFacesPriorityRoom, priorityRoomId). The room copy
 * contributes its own id, the FK back to structure, the room-wall
 * parentWallIds, and the named per-category trim fields.
 *
 * Spread order is load-bearing: spreading `structureOpening` first means
 * a stale room-copy `center` / `doorType` / etc. cannot leak in. We then
 * explicitly restore the three room-side identity fields and merge only
 * the named trim into the category sub-object.
 *
 * Two transitional accommodations live here through P3b; both deleted
 * in P3c once the migration has reduced their counts to zero.
 */
const resolveRoomOpening = (roomOpening, structureOpening) => {
  // P1 transitional (deleted in P3c): when no structure counterpart
  // exists, return the room copy as-is so legacy orphan room copies
  // still render during the rollout. After P3b's FK check confirms
  // zero orphans, this tightens to `return null`.
  if (!structureOpening) return roomOpening;

  const categoryKey = Object.keys(structureOpening.category || {})[0];
  if (!categoryKey) return roomOpening;

  const trimFields = TRIM_FIELDS_BY_CATEGORY[categoryKey] || [];
  const roomCategory = roomOpening.category?.[categoryKey] || {};
  const roomTrim = pickFields(roomCategory, trimFields);

  // P1 transitional (deleted in P3c): trim used to live nested inside
  // doorStyle / windowStyle. After P3b's migration hoists every room
  // copy this fallback becomes dead.
  const styleKey =
    categoryKey === 'door'
      ? 'doorStyle'
      : categoryKey === 'window'
        ? 'windowStyle'
        : null;
  const legacyStyle = styleKey ? roomCategory[styleKey] : null;
  const legacyTrim = legacyStyle ? pickFields(legacyStyle, trimFields) : {};
  const effectiveRoomTrim = { ...legacyTrim, ...roomTrim };

  // P5 transitional (deleted alongside the appearance-hoist migration's
  // post-run check at zero): structure-side appearance used to live
  // nested inside `category[type].doorStyle` / `windowStyle`. Readers
  // (`Door.jsx`, `Window.jsx`) now read flat; hoist on-the-fly so legacy
  // structures render correctly until `migrate-opening-shape` sweeps.
  // The hoist only FILLS what the flat fields lack: every writer writes
  // flat, so when both are present the nested copy is the older one. It
  // used to be spread over the flat fields, and a door restyled on a scan
  // that still carried the nested copy came back unedited on reload.
  const structureCategory = structureOpening.category?.[categoryKey] || {};
  const structureNestedStyle = styleKey ? structureCategory[styleKey] : null;
  const appearanceFields = APPEARANCE_FIELDS_BY_CATEGORY[categoryKey] || [];
  const legacyStructureAppearance = structureNestedStyle
    ? pickFields(structureNestedStyle, appearanceFields)
    : {};

  // P5 transitional (deleted with Pass 8 post-run check at zero): trim was
  // single-valued before the per-side expansion. Surface legacy single
  // fields as both Side A and Side B on the resolved opening so renderers
  // that read per-side variants don't see undefined on unmigrated data.
  const legacySingleTrim = pickLegacySideExpansion(roomCategory, categoryKey);

  return {
    ...structureOpening,
    id: roomOpening.id,
    structureOpeningId: roomOpening.structureOpeningId,
    parentWallIds: roomOpening.parentWallIds,
    category: {
      ...structureOpening.category,
      [categoryKey]: {
        ...legacyStructureAppearance, // nested appearance, where flat has none
        ...structureCategory,
        ...legacySingleTrim, // per-side expansion of legacy single fields
        ...effectiveRoomTrim,
      },
    },
  };
};

// Symmetric (both sides) expansion — trim should default identical on both
// faces unless the user explicitly overrides per-side.
const LEGACY_SINGLE_TRIM_BY_CATEGORY = {
  door: ['trimStyle'],
  window: ['trimStyle', 'trimColor', 'trimWidth', 'trimDepth'],
  opening: [],
};
// Exterior-only (Side B) expansion — legacy single sill maps to an exterior
// weather sill, matching the pre-Phase-5 single-sill orientation (lone sill
// at -Z with bevel facing outside). An interior stool requires explicit
// per-side authoring.
const LEGACY_SINGLE_SILL_BY_CATEGORY = {
  door: [],
  window: ['sillDepth', 'sillColor'],
  opening: [],
};

const pickLegacySideExpansion = (roomCategory, categoryKey) => {
  const out = {};
  const trim = LEGACY_SINGLE_TRIM_BY_CATEGORY[categoryKey] || [];
  for (const f of trim) {
    if (roomCategory?.[f] === undefined) continue;
    const v = roomCategory[f];
    out[`${f}SideA`] = v;
    out[`${f}SideB`] = v;
  }
  const sill = LEGACY_SINGLE_SILL_BY_CATEGORY[categoryKey] || [];
  for (const f of sill) {
    if (roomCategory?.[f] === undefined) continue;
    out[`${f}SideB`] = roomCategory[f];
  }
  return out;
};

module.exports = {
  DOOR_TRIM_FIELDS,
  WINDOW_TRIM_FIELDS,
  TRIM_FIELDS_BY_CATEGORY,
  APPEARANCE_FIELDS_BY_CATEGORY,
  pickFields,
  buildOpeningLookup,
  hydrateRoomOpenings,
  resolveRoomOpening,
  pickLegacySideExpansion,
};
